import { Intent } from '@blueprintjs/core';
import * as React from 'react';
import { useQueries, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';

import { useAppDispatch } from 'src/ui/app/hooks';
import { isTrackingUpToDate } from 'src/ui/containers/endpoints/utils/useSduTrackingStatus';
import { queueMessage } from 'src/ui/features/notifications';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import {
  Connection,
  GetPlatformTrackingStatusDocument,
  GetPlatformTrackingStatusQuery,
  useSyncPlatformSduUsageMutation,
} from 'src/ui/graph/types';
import { previousConnectionData } from 'src/ui/hooks/previousConnectionData';
import * as copy from 'src/ui/templates/copy';
import { formatLastSyncedTimestamp } from 'src/ui/utils/dateUtils';

export type SyncAllConnection = Pick<Connection, 'id' | 'token'>;

export interface ConnectionsSyncStatus {
  // Formatted "most recent updated timestamp" across all passed connections.
  lastSyncedLabel: string | null;
  // Connection IDs whose tracking-status metric_date is yesterday UTC — these
  // are fully up to date and can be skipped on the next sync.
  completedConnectionIds: Set<string>;
}

export const useConnectionsSyncStatus = (
  connections: SyncAllConnection[]
): ConnectionsSyncStatus => {
  const { org = '' } = useParams<{ org?: string }>();
  const queryClient = useQueryClient();
  const trackingQueries = useQueries(
    connections.map((c) => {
      const keyPrefix = ['getPlatformTrackingStatus', org, c.id];
      return {
        queryFn: () =>
          graphQLClient.request<GetPlatformTrackingStatusQuery>({
            document: GetPlatformTrackingStatusDocument,
            variables: { connectionId: c.id, org_domain: org },
          }),
        queryKey: [...keyPrefix, c.token ?? null],
        placeholderData: () =>
          previousConnectionData<GetPlatformTrackingStatusQuery>(
            queryClient,
            keyPrefix
          ),
      };
    })
  );

  const updatedMillis: number[] = [];
  const completedConnectionIds = new Set<string>();

  trackingQueries.forEach((q, idx) => {
    const status = q.data?.getPlatformTrackingStatus;
    const updated = status?.updated;
    if (updated) {
      const parsed = Date.parse(updated);
      if (!Number.isNaN(parsed)) {
        updatedMillis.push(parsed);
      }
    }
    if (isTrackingUpToDate(status)) {
      completedConnectionIds.add(connections[idx].id);
    }
  });

  const lastSyncedLabel = !updatedMillis.length
    ? null
    : formatLastSyncedTimestamp(new Date(Math.max(...updatedMillis)));

  return { lastSyncedLabel, completedConnectionIds };
};

export interface SyncAllConnectionsResult {
  isSyncing: boolean;
  syncAll: () => Promise<void>;
}

export const useSyncAllConnections = (
  connections: SyncAllConnection[]
): SyncAllConnectionsResult => {
  const { org = '' } = useParams<{ org?: string }>();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const [isSyncing, setIsSyncing] = React.useState(false);

  const isSyncingRef = React.useRef(false);

  const { mutateAsync: syncPlatformSduUsage } = useSyncPlatformSduUsageMutation(
    graphQLClient
  );

  const syncAll = React.useCallback(async () => {
    if (!connections.length || isSyncingRef.current) {
      return;
    }
    isSyncingRef.current = true;
    setIsSyncing(true);
    try {
      // Sequential rather than parallel — N concurrent sync jobs would
      // spike the platform's worker pool for no UX benefit (the row is
      // gated by a single spinning icon either way).
      const failures = await connections.reduce<Promise<number>>(
        async (prevFailures, c) => {
          const acc = await prevFailures;
          try {
            const result = await syncPlatformSduUsage({
              connectionId: c.id,
              org_domain: org,
            });
            if (result?.syncPlatformSduUsage?.status !== 'success') {
              return acc + 1;
            }
            return acc;
          } catch {
            return acc + 1;
          }
        },
        Promise.resolve(0)
      );

      queryClient.invalidateQueries(['getPlatformTrackingStatus']);
      queryClient.invalidateQueries(['getTotalUnitUsage']);
      queryClient.invalidateQueries(['getUnitUsage']);
      queryClient.invalidateQueries(['getEndpointLastSynced']);

      if (failures === connections.length) {
        dispatch(
          queueMessage({
            intent: Intent.DANGER,
            message: copy.components.sduDashboard.syncAll.failed,
          })
        );
      } else if (failures > 0) {
        dispatch(
          queueMessage({
            intent: Intent.WARNING,
            message: copy.components.sduDashboard.syncAll.partial(
              connections.length - failures,
              connections.length
            ),
          })
        );
      } else {
        dispatch(
          queueMessage({
            intent: Intent.SUCCESS,
            message: copy.components.sduDashboard.syncAll.success,
          })
        );
      }
    } finally {
      isSyncingRef.current = false;
      setIsSyncing(false);
    }
  }, [connections, dispatch, org, queryClient, syncPlatformSduUsage]);

  return { isSyncing, syncAll };
};
